<template>
  <v-container v-if="showComponent">
    <!-- Client Details Card -->
    <v-card class="mb-4">
      <v-card-title>
        <h2 :title="'Client id=' + clientDetails.id">{{ clientDetails.name }}({{clientDetails.id}})</h2>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6" style="font-size: 1.2em;">
            <p><strong>Short Name:</strong> {{ clientDetails.short_name }}</p>
            <p><strong>Status:</strong> <v-btn small class="pa-3" :color="clientDetails.status=='suspended'?'red':''" @click="showStatusActions(clientDetails.id)">{{ clientDetails.status }}</v-btn></p>
            <p class="mt-2" :title="'Description=' + clientDetails.client_type_description"><strong>Type:</strong> {{ clientDetails.client_type_name }}</p>
          </v-col>
          <v-col cols="12" sm="6"  style="font-size: 1.2em;">
            <p><strong>Email:</strong> {{ clientDetails.notification_email || 'N/A' }}</p>
            <p><strong>Phone:</strong> {{ clientDetails.phone || 'N/A' }}</p>
            <p><strong>Current Debit:</strong> {{ clientDetails.current_debit }}</p>
            <p><strong>Current Credit:</strong> {{ clientDetails.current_credit }}</p>
            <!-- Is Distributor Toggle -->
            <div class="mt-2">
              <DistributorToggle
                :client-id="parseInt(client_id)"
                :model-value="isDistributor"
                :token="token"
                :disabled="!canManageDistributor"
                @updated="(val) => { isDistributor = val; clientDetails.is_distributor = val; }"
                @error="(msg) => alert(msg)"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pa-4 pt-0">
        <v-btn
          color="primary"
          variant="elevated"
          @click="showEditDialog = true"
        >
          <v-icon class="mr-1">mdi-pencil</v-icon>
          Editeaza
        </v-btn>
        <v-btn
          color="info"
          variant="outlined"
          :to="`/comments?comment_type_id=4&element_id=${clientDetails.id}`"
        >
          <v-icon class="mr-1">mdi-comment-text-multiple</v-icon>
          {{ t.comments?.view_comments || 'View Comments' }}
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="elevated"
          class="ml-2"
          :to="`/users?client_id=${clientDetails.id}`"
        >
          <v-icon class="mr-1">mdi-account-group</v-icon>
          Users
        </v-btn>
      </v-card-actions>

      <!-- Edit Client Dialog -->
      <EditClientDialog
        v-model="showEditDialog"
        :client-id="client_id"
        @saved="onClientEditSaved"
        @close="showEditDialog = false"
      />
    </v-card>

    <!-- Services Section -->
    <v-card class="mb-4">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex ga-2">
          <v-btn
            color="primary"
            variant="elevated"
            :to="`/clientServices?subclient_id=${client_id}`"
          >
            <v-icon class="mr-1">mdi-magnify</v-icon>
            Servicii Client
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            @click="showAddNewClientServiceEvent()"
          >
            <v-icon class="mr-1">mdi-plus</v-icon>
            Adauga Serviciu
          </v-btn>
        </div>
        <v-btn
          variant="text"
          size="small"
          @click="showStatistics = !showStatistics"
        >
          <v-icon class="mr-1">{{ showStatistics ? 'mdi-chevron-up' : 'mdi-chart-bar' }}</v-icon>
          {{ showStatistics ? 'Ascunde Statistici' : 'Statistici' }}
        </v-btn>
      </v-card-title>

      <!-- Client Services Stats - collapsible -->
      <v-expand-transition>
        <v-card-text v-if="showStatistics">
          <ClientServicesStats
            v-if="client_id && token"
            :client-id="client_id"
            :token="token"
            :include-subclients="false"
          />
        </v-card-text>
      </v-expand-transition>
    </v-card>

    <v-card v-if="clientSubclients.length>0" class="mt-4">
      <v-card-title>
        <v-row class="justify-space-between">
          <v-btn title="Click to show clients list" @click="showSubclientsTable=!showSubclientsTable">Clients</v-btn>
          <v-btn title="Click to show clients list" color="green" @click="showAddNewClientEvent">Add New Client</v-btn>
        </v-row>
      </v-card-title>
      <v-card-text v-if="showSubclientsTable">
        <v-data-table
            :headers="subclientsHeaders"
            :items="clientSubclients"
            item-value="id"
            class="elevation-1"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn class="ps-10" :color="item.status=='suspended'?'red':''" @click="gotoClientDetails(item.id)">
              Select
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-card v-else>
      <v-card-title>
        <v-btn @click="showClientUsersTable=!showClientUsersTable">No clients</v-btn>
      </v-card-title>
    </v-card>

    <v-card v-if="clientUsers.length>0" class="mt-4">
      <v-card-title>
        <v-btn @click="showClientUsersTable=!showClientUsersTable">Users</v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
            v-if="showClientUsersTable && clientUsers.length > 0"
            :headers="usersHeaders"
            :items="clientUsers"
            item-value="id"
            class="elevation-1"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <v-btn color="success" variant="elevated" @click="goToUserDetailsView(item)">Details</v-btn>
          </template>
        </v-data-table>

      </v-card-text>
    </v-card>
    <v-card v-else>
        <v-card-title>
          <v-btn @click="showClientUsersTable=!showClientUsersTable">No users</v-btn>
        </v-card-title>
      </v-card>

    <!-- Delivery Addresses Section -->
    <ClientDeliveryAddresses
      v-if="client_id && token"
      :client-id="client_id"
      class="mt-4"
      @addressesUpdated="onAddressesUpdated"
    />
  </v-container>
</template>


<script>
import {getClientDetails} from "@/composables/useGetClientsAPI";
import store from "@/store";
import {log} from "@/composables/useLog";
import ClientServicesStats from "@/components/ClientServicesStats.vue"
import ClientDeliveryAddresses from "@/components/ClientDeliveryAddresses.vue"
import EditClientDialog from "@/components/EditClientDialog.vue"
import DistributorToggle from "@/components/selects/DistributorToggle.vue"
import useGetLoggedIn from "@/composables/useGetLoggedIn";
import { t as tRef } from "@/composables/useLanguages";
export default {
  name: "ClientsDetails",
  emits: ["showAddNewClientEvent","showAddNewClientServiceEvent","showClientDetailsReadyEvent","reloadClientDetails"],
  components: {
    ClientServicesStats,
    ClientDeliveryAddresses,
    EditClientDialog,
    DistributorToggle
  },
  computed: {
    t() {
      return tRef.value;
    },
    canManageDistributor() {
      // Can manage if user is root, admin_distributor, or default_admin_l0 for own client
      const isRoot = this.user?.admin || this.currentUserRoles.includes('root');
      const isAdminDistributor = this.currentUserRoles.includes('admin_distributor');
      const isDefaultAdminL0ForOwnClient = this.currentUserRoles.includes('default_admin_l0') &&
        this.currentUserClientId === parseInt(this.client_id);
      console.log("canManageDistributor - isRoot:", isRoot, "isAdminDistributor:", isAdminDistributor, "isDefaultAdminL0ForOwnClient:", isDefaultAdminL0ForOwnClient);
      return isRoot || isAdminDistributor || isDefaultAdminL0ForOwnClient;
    }
  },
  data() {
    return {
      token:null,
      showComponent:false,
      showSubclientsTable:false,
      showStatistics:false,
      showEditDialog:false,
      showClientUsersTable:false,
      itemsPerPage:10,
      search: '',
      clientsListSize:0,
      client_type:"",
      page:1,
      loading:false,
      sortBy:"",
      user:store.getters.getUser,
      clientDetails: {},
      clientUsers: [],
      clientSubclients: [],
      clientObject: {},
      subclientsHeaders: [
        { key: 'id', title: 'ID', maxWidth:"50px" },
        { key: 'name', title: 'Name', maxWidth:"200px"},
        { key: 'short_name', title: 'ShortName', maxWidth:"300px"},
        { key: 'actions', title: 'Actions', maxWidth:"300px"},
      ],
      usersHeaders: [
        { key: 'id', title: 'ID', maxWidth:"50px" },
        { key: 'username', title: 'Username', maxWidth:"200px"},
        { key: 'name', title: 'Name', maxWidth:"200px"},
        { key: 'email', title: 'Email', maxWidth:"300px"},
        { key: 'phone', title: 'Phone', maxWidth:"200px"},
        { key: 'actions', title: 'Action', maxWidth:"200px"},
      ],
      // Distributor toggle
      isDistributor: false,
      savingDistributor: false,
      currentUserRoles: [],
      currentUserClientId: null,
    };
  },
  props:["client_id"],
  methods: {
    goToUserDetailsView(user) {
      // alert(user.id)
      this.selectedUser = user;
      store.commit('setSelectedUser', user)
      // const router=useRouter()
      this.$router.push({ name: 'userDetails' });
    },
    showStatusActions (client_id) {
      alert("list of actions for client_id:"+ client_id)
    },

    showAddNewClientEvent() {
      log("emitem showAddNewClientEvent la ClientInfo sa aprinda AddNewClient")
      this.$emit("showAddNewClientEvent")
    },

    showAddNewClientServiceEvent() {
      log("emitem showAddNewClientServiceEvent la ClientInfo sa aprinda showAddNewClientServiceEvent")
      log("emitem cliendDetails object")
      log(this.clientDetails)
      this.$emit("showAddNewClientServiceEvent", this.clientDetails)
    },

    editItem (item) {
      this.editedIndex = this.clients.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialog = true
    },

    deleteItem (item) {
      this.editedIndex = this.clients.indexOf(item)
      this.editedItem = Object.assign({}, item)
      this.dialogDelete = true
    },
    deleteItemConfirm () {
      this.desserts.splice(this.editedIndex, 1)
      this.closeDelete()
    },
    gotoClientDetails(client_id){
      log("welcome in gotoClientDetails cu client_id=" + client_id)
      // this.client_id=client_id
      // this.loadClientDetails()
      this.$emit("reloadClientDetails", client_id)
      // this.$router.replace({ name: "client", query: { client_id } });
    },
    navigateToClientDetails(client_id) {
      // this.$router.push({ path: "clientDetails", params: { id: clientId } });
      alert(`Active action clicked for client: ${client_id}`);
    },
    handleActiveAction(client) {
      alert(`Active action clicked for client: ${client.name}`);
    },
    handleInactiveAction(client) {
      alert(`Active action clicked for client: ${client.name}`);
    },
    loadClientDetails () {
      this.loading = true
      // log (this.page)
      // log (this.itemsPerPage)
      // log (this.sortBy)
      log("am citit in loadClientDetails token din store si am primit:  this.client_id="+ this.client_id)
      // setTimeout(() => {
      // alert(this.client_id)
      getClientDetails(this.token, this.client_id).then((response) => {
        console.log("getClientDetails response:", response);
        this.clientDetails = response.client_details;
        this.clientSubclients = response.subclients;
        this.clientUsers = response.users;
        // Set distributor toggle value
        this.isDistributor = response.client_details?.is_distributor || false;
        console.log("isDistributor:", this.isDistributor);
        console.log("clientDetails.is_distributor:", response.client_details?.is_distributor);
        log("this.clientSubclients=>")
        log(this.clientSubclients)
        log("this.clientUsers=>")
        this.loading = false
        this.showComponent = true
        // alert("back from getClientsListApi, emitem semnalul!")
        this.$emit("showClientDetailsReadyEvent")
      })
      // },500)
    },
    openUserRoles(user_id){
      alert(user_id)
    },
    onAddressesUpdated() {
      log("Delivery addresses updated");
    },
    onClientEditSaved() {
      log("Client updated, reloading details");
      this.loadClientDetails();
    },
    async toggleDistributor(newValue) {
      this.savingDistributor = true;
      try {
        const { updateClientDistributorStatus } = await import("@/composables/useGetClientsAPI");
        const response = await updateClientDistributorStatus(this.token, this.client_id, newValue);
        if (response.error) {
          // Revert the toggle
          this.isDistributor = !newValue;
          alert(response.message || 'Error updating distributor status');
        } else {
          // Update local clientDetails
          this.clientDetails.is_distributor = newValue;
        }
      } catch (err) {
        log("Error toggling distributor status:", err);
        this.isDistributor = !newValue;
        alert('Error updating distributor status');
      } finally {
        this.savingDistributor = false;
      }
    }
  },
  created() {
    // alert("created Clientdetails")
    // this.token=store.getters.getToken
    // log("in created am citit token din store si am primit: ")
    // log(this.token)
    const { getLoggedIn, user, loggedIn, token } = useGetLoggedIn();
    getLoggedIn().then(() => {
          // Logs from the provided instructions
          log("dupa created in Dashboard avem:");
          log("autorizare in Dashboard after getLoggedIn");
          log("user:");
          log(user);
          log("loggedIn: " + loggedIn.value);
          log("token: " + token.value);
          this.clientID=user.client_id
          this.token = this.$store.getters.getToken; // Setting token from store
          // Store user roles and client_id for permission checks
          this.currentUserRoles = user.value?.roles || [];
          this.currentUserClientId = user.value?.client_id;
          console.log("user.value:", user.value);
          console.log("currentUserRoles:", this.currentUserRoles);
          console.log("currentUserClientId:", this.currentUserClientId);
          console.log("user.admin:", this.user?.admin);
          this.loadClientDetails()
    })
  },
  mounted(){

  }
}
</script>


<style>
.width100px{
  width:100px;
}
</style>